import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Typography } from '@mui/material';
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import {
  StripeAddressElementChangeEvent,
  StripeElements,
  StripeError,
  StripePaymentElementChangeEvent
} from '@stripe/stripe-js';
import { errorCodes, getStripeErrorMessages } from 'configs/stripeConfig';
import InstavisionButton from 'coreComponents/Button/Button';
import analyticsService, {
  AppEvent
} from 'infrastructure/serviceHandlers/Analytics/Analytics';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TProps_PaymentScreen } from 'typings';

export default function StripeForm(props: TProps_PaymentScreen) {
  const {
    submitPurchaseRequest,
    amount,
    isUpgrade,
    isFreeTrial,
    isCancellationAndRefundPolicyAccepted = true,
    planSelected,
    isPreviewPlan
  } = props;
  const stripe = useStripe();
  const elements: StripeElements | null = useElements();

  const applePayRecurringPaymentRequest = {
    managementURL: `${window.location.origin}/billings`,
    paymentDescription: planSelected?.name
      ? `${planSelected?.name} ${
          planSelected.subscription_cycle === 12 ? 'Yearly' : 'Monthly'
        } Payment`
      : 'Add a payment method',
    ...(isFreeTrial && {
      trialBilling: {
        amount: 0,
        label: planSelected?.name
          ? `${planSelected?.name} - ${
              planSelected?.subscription_cycle === 12 ? 'Yearly' : 'Monthly'
            }`
          : 'Trial Billing'
      }
    }),
    regularBilling: {
      amount: amount < 0 ? 0 : amount,
      label: planSelected?.name
        ? `${planSelected?.name} - ${
            planSelected?.subscription_cycle === 12 ? 'Yearly' : 'Monthly'
          }`
        : 'Regular Billing'
    }
  };

  console.log(
    'apple pay config',
    applePayRecurringPaymentRequest,
    { amount },
    { planSelected },
    {
      isFreeTrial
    }
  );

  const { t } = useTranslation('translation', {
    keyPrefix: 'views.paymentScreen'
  });
  const errorMessages = getStripeErrorMessages(t);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');
  const [addressErrorMessage, setAddressErrorMessage] = useState<
    string | undefined
  >('');
  const [loading, setLoading] = useState(false);

  const handleError = (error: StripeError) => {
    setLoading(false);
    if (error.decline_code === errorCodes.GENERIC_DECLINE) {
      setAddressErrorMessage(errorMessages.ADDRESS_MISMATCH);
      return;
    }
    setErrorMessage(error.message);
  };

  const handleSubmit = async (event: React.SyntheticEvent) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setErrorMessage('');
    setAddressErrorMessage('');
    setLoading(true);

    // Trigger form validation and wallet collection
    if (!elements) {
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    // Confirm the SetupIntent using the details collected by the Payment Element
    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required'
    });
    analyticsService.trackEvent(AppEvent.ADD_NEW_CARD);
    console.log('setup intent error', error);
    if (!isFreeTrial && !isUpgrade) {
      analyticsService.trackEvent(AppEvent.SUBSCRIPTION_PURCHASE_FAILURE, {
        stripe_intent_setup_error: error?.message || null
      });
    } else if (!isFreeTrial && isUpgrade) {
      analyticsService.trackEvent(AppEvent.SUBSCRIPTION_UPGRADE_FAILURE, {
        stripe_intent_setup_error: error?.message || null
      });
    }

    if (error) {
      // This point is only reached if there's an immediate error when
      // confirming the setup. Show the error to your customer (for example, payment details incomplete)
      handleError(error);
    } else {
      console.log('setupIntent', setupIntent);
      const { payment_method } = setupIntent || {};
      if (typeof payment_method === 'string') {
        submitPurchaseRequest(payment_method);
      }
      // alert('payment success');
      // Your customer is redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer is redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
    setLoading(false);
  };

  const handlePaymentElementChange = (
    event: StripePaymentElementChangeEvent | StripeAddressElementChangeEvent
  ) => {
    if (event.complete) {
      setErrorMessage('');
      setAddressErrorMessage('');
    }

    // Check if it's a PaymentElement event and has a 'type'
    if ('value' in event && 'type' in event.value) {
      setSelectedPaymentMethod(event.value.type);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        {selectedPaymentMethod === 'card' && (
          <Box
            sx={{
              marginBottom: '6px'
            }}
          >
            <AddressElement
              options={{
                mode: 'billing',
                autocomplete: { mode: 'disabled' },
                fields: {
                  phone: 'auto'
                }
              }}
              onChange={handlePaymentElementChange}
            />
            {addressErrorMessage && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'self-start',
                  marginY: '16px',
                  gap: '10px'
                }}
              >
                <CancelIcon sx={{ color: 'error.main' }} />
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'start'
                  }}
                >
                  {addressErrorMessage}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        <PaymentElement
          onChange={handlePaymentElementChange}
          options={{
            applePay: {
              recurringPaymentRequest: applePayRecurringPaymentRequest
            },
            paymentMethodOrder: ['applePay', 'googlePay', 'card']
            // ...(amount < 0 && {
            //   paymentMethodOrder: ['card'],
            //   wallets: {
            //     applePay: 'never',
            //     googlePay: 'never'
            //   }
            // })
          }}
        />

        <Typography
          variant="body2"
          sx={{ marginTop: '20px' }}
          color="secondary.main"
        >
          {errorMessages.NO_PREPAID_CARDS}
        </Typography>
        {amount < 0 ? (
          <InstavisionButton
            sx={{
              width: '100%',
              marginTop: '20px',
              padding: '8px 0'
            }}
            type="submit"
            disabled={!stripe || loading}
          >
            {t('addCard')}
          </InstavisionButton>
        ) : (
          <InstavisionButton
            sx={{
              width: '100%',
              marginTop: '20px'
            }}
            type="submit"
            disabled={
              !stripe || loading || !isCancellationAndRefundPolicyAccepted
            }
          >
            {/* {isFreeTrial && !isUpgrade && !isPreviewPlan && 'Confirm Trial'} */}
            {isFreeTrial && isUpgrade && t('upgradeFreeTrial')}
            {(!isFreeTrial || isPreviewPlan) &&
              `${t('pay')} $${amount && amount / 100}`}
          </InstavisionButton>
        )}

        {errorMessage && (
          <Typography
            variant="body2"
            sx={{
              margin: '15px auto',
              color: (theme) => theme.palette.error.main
            }}
          >
            {errorMessage}
          </Typography>
        )}
      </form>
    </div>
  );
}
